import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const NAV = [
  { path: '/', label: 'Inicio', exact: true, icon: (active) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? 'var(--accent)' : 'var(--text-muted)'} stroke="none">
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
    </svg>
  )},
  { path: '/transactions', label: 'Movimientos', icon: (active) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round">
      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  )},
  { path: '/budgets', label: 'Presupuesto', icon: (active) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  )},
  { path: '/savings', label: 'Ahorros', icon: (active) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round">
      <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8zm1-13h-2v6l5 3 1-1.73-4-2.27z"/>
    </svg>
  )},
  { path: '/debts', label: 'Deudas', icon: (active) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round">
      <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  )},
  { path: '/categories', label: 'Categorías', icon: (active) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round">
      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
      <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
    </svg>
  )},
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Sesión cerrada');
  };

  return (
    <div style={styles.root}>
      {/* Top header */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.brand}>
            <div style={styles.brandIcon}>₿</div>
            <span style={styles.brandName}>Finanzas</span>
          </div>
          <div style={styles.headerRight}>
            <span style={styles.userName}>Hola, {user?.name?.split(' ')[0]}</span>
            <button onClick={handleLogout} className="btn btn-ghost btn-sm" title="Cerrar sesión">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Page content */}
      <main style={styles.main}>
        <Outlet />
      </main>

      {/* Bottom nav */}
      <nav style={styles.nav}>
        {NAV.map(({ path, label, icon, exact }) => (
          <NavLink
            key={path}
            to={path}
            end={exact}
            style={({ isActive }) => ({
              ...styles.navItem,
              ...(isActive ? styles.navItemActive : {}),
            })}
          >
            {({ isActive }) => (
              <>
                <span style={styles.navIcon}>
                  {icon(isActive)}
                </span>
                <span style={{
                  ...styles.navLabel,
                  ...(isActive ? styles.navLabelActive : {}),
                }}>
                  {label}
                </span>
                {isActive && <span style={styles.navDot} />}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

const styles = {
  root: { display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '-webkit-fill-available' },
  header: {
    position: 'sticky', top: 0, zIndex: 100,
    background: 'rgba(10,10,15,0.85)', backdropFilter: 'blur(20px)',
    borderBottom: '1px solid var(--border-light)',
    height: 'calc(var(--header-height) + env(safe-area-inset-top))',
    paddingTop: 'env(safe-area-inset-top)',
  },
  headerInner: {
    maxWidth: 900, margin: '0 auto', padding: '0 20px', height: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 10 },
  brandIcon: {
    width: 34, height: 34, borderRadius: 10,
    background: 'linear-gradient(135deg, var(--accent), #00D4AA)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16, fontWeight: 700,
  },
  brandName: { fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 12 },
  userName: { fontSize: '0.85rem', color: 'var(--text-secondary)' },
  main: {
    flex: 1,
    padding: '28px 20px',
    paddingBottom: 'calc(var(--nav-height) + max(env(safe-area-inset-bottom), 16px) + 32px)',
    maxWidth: 900, margin: '0 auto', width: '100%',
  },
  nav: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: 'rgba(17,17,24,0.95)', backdropFilter: 'blur(20px)',
    borderTop: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-around',
    padding: '0 8px',
    paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
    height: 'calc(var(--nav-height) + max(env(safe-area-inset-bottom), 16px))',
    zIndex: 100,
  },
  navItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
    textDecoration: 'none', flex: 1, padding: '8px 4px', borderRadius: 12,
    position: 'relative', transition: 'all 0.2s',
  },
  navItemActive: {},
  navIcon: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s', lineHeight: 1,
  },
  navLabel: {
    fontSize: '0.65rem', fontWeight: 500, color: 'var(--text-muted)', transition: 'all 0.2s',
    letterSpacing: '0.01em',
  },
  navLabelActive: { color: 'var(--accent)' },
  navDot: {
    position: 'absolute', bottom: 2, width: 4, height: 4,
    borderRadius: '50%', background: 'var(--accent)',
  },
};
